import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Twitter, Sparkles } from "lucide-react";
import { BRAND, NAV_LINKS, SERVICES, BRANCHES, UPCOMING_BRANCHES } from "../lib/constants";
import { useTranslation } from "../lib/i18n";
import LogoMark from "./LogoMark";

const NAV_KEY = {
  "/": "nav.home",
  "/services": "nav.services",
  "/destinations": "nav.destinations",
  "/corporate-solutions": "nav.corporate",
  "/about": "nav.about",
  "/contact": "nav.contact",
};

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer
      data-testid="site-footer"
      className="relative border-t border-white/5 bg-[#05070C] pt-20 pb-10"
    >
      <div className="absolute inset-0 bg-radial-gold pointer-events-none" />
      <div className="container-x relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4 space-y-6">
            <LogoMark size="lg" />
            <p className="text-white/60 text-sm leading-relaxed max-w-sm">
              {BRAND.tagline}. {t("footer.taglineExtra")}
            </p>
            <div className="flex items-center gap-4 pt-2">
              {[
                { icon: Instagram, href: BRAND.social.instagram, label: "instagram" },
                { icon: Facebook, href: BRAND.social.facebook, label: "facebook" },
                { icon: Linkedin, href: BRAND.social.linkedin, label: "linkedin" },
                { icon: Twitter, href: BRAND.social.twitter, label: "twitter" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`footer-social-${s.label}`}
                  aria-label={s.label}
                  className="h-10 w-10 grid place-items-center rounded-full border border-white/10 text-white/70 hover:text-gold hover:border-gold/60 transition-colors"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-5">{t("footer.explore")}</h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm text-white/65 hover:text-gold transition-colors"
                  >
                    {t(NAV_KEY[l.to] || l.label)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <h4 className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-5">{t("footer.services")}</h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/services/${s.id}`}
                    data-testid={`footer-service-${s.id}`}
                    className="text-sm text-white/65 hover:text-gold transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-5">{t("footer.ourBranches")}</h4>
            <div className="space-y-6">
              {BRANCHES.map((b) => (
                <div key={b.id} data-testid={`footer-branch-${b.id}`}>
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-base leading-none" aria-hidden="true">{b.flag}</span>
                    <span className="text-white text-sm font-medium">
                      {b.city}, {b.country}
                    </span>
                  </div>
                  <ul className="space-y-2 text-sm text-white/70 pl-6">
                    <li className="flex gap-2">
                      <MapPin className="h-3.5 w-3.5 text-gold mt-1 shrink-0" />
                      <span className="leading-relaxed">{b.address}</span>
                    </li>
                    <li className="flex gap-2">
                      <Phone className="h-3.5 w-3.5 text-gold mt-1 shrink-0" />
                      <div className="flex flex-col">
                        {b.phones.map((p) => (
                          <a
                            key={p}
                            href={`tel:${p.replace(/\s+/g, "")}`}
                            className="hover:text-gold transition-colors"
                          >
                            {p}
                          </a>
                        ))}
                      </div>
                    </li>
                    <li className="flex gap-2">
                      <Mail className="h-3.5 w-3.5 text-gold mt-1 shrink-0" />
                      <div className="flex flex-col">
                        {b.emails.map((e) => (
                          <a
                            key={e}
                            href={`mailto:${e}`}
                            className="hover:text-gold transition-colors break-all"
                          >
                            {e}
                          </a>
                        ))}
                      </div>
                    </li>
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-10 border-t border-white/5 grid md:grid-cols-2 gap-8 lg:gap-12">
          <div data-testid="footer-current-branches">
            <div className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-4">
              {t("footer.currentBranches")}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/80">
              {BRANCHES.map((b) => (
                <span key={b.id} className="flex items-center gap-2">
                  <span className="text-lg leading-none" aria-hidden="true">{b.flag}</span>
                  <span>
                    {b.country} <span className="text-white/40">— {b.city}</span>
                  </span>
                </span>
              ))}
            </div>
          </div>

          <div data-testid="footer-upcoming-branches">
            <div className="text-[11px] uppercase tracking-[0.32em] text-gold/90 mb-4 flex items-center gap-2">
              <Sparkles className="h-3 w-3" />
              {t("footer.upcomingBranches")}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70">
              {UPCOMING_BRANCHES.map((u) => (
                <span key={u.id} className="flex items-center gap-2">
                  <span className="text-lg leading-none" aria-hidden="true">{u.flag}</span>
                  <span>{u.country}</span>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="gold-rule mt-16 mb-8" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/45">
          <p>© {new Date().getFullYear()} {BRAND.name}. {t("footer.copyright")}</p>
          <p className="tracking-widest uppercase">{t("footer.crafted")}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
